// @file Surface user contributors store
// TODO: [to-be-migrated][post]
import { captureFetchException } from '@@/bits/error_tracker'
import { getVuexStore } from '@@/bits/pinia'
import { unboundedWatch, vSet } from '@@/bits/vue'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { HashId, Id, User, WallId } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { sortBy } from 'es-toolkit'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useSurfaceUserContributorsStore = defineStore('surfaceUserContributors', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */

  const userById = ref<Record<Id, User>>({})
  const userByHashId = ref<Record<HashId, User>>({})
  const userIdsBeingFetched = ref<Array<Id | HashId>>([])
  const xContributorsList = ref(false)

  /* ---------------------- */
  /* GETTERS                */
  /* ---------------------- */

  const areAuthorsFetched = computed(() => userIdsBeingFetched.value.length === 0)
  const postAuthorIds = computed<Id[]>(() => surfaceVuexStore?.getters['post/postAuthorIds'])
  const postAuthors = computed<User[]>(() =>
    Object.values(userById.value).filter((u) => postAuthorIds.value.includes(u.id)),
  )
  const postAuthorsById = computed<Record<Id, User>>(() => {
    const authors = postAuthors.value
    return authors.reduce<Record<Id, User>>((acc, author) => {
      acc[author.id] = author
      return acc
    }, {})
  })
  const postAuthorsByHashId = computed<Record<HashId, User>>(() => {
    return postAuthors.value.reduce<Record<HashId, User>>((acc, author) => {
      if (author.hashid) {
        acc[author.hashid] = author
      }
      return acc
    }, {})
  })
  const registeredPostAuthors = computed<User[]>(() =>
    sortBy(
      postAuthors.value.filter((u) => u.registered),
      [
        (u: User): string | undefined => {
          return surfaceVuexStore?.getters.builder.id === u.id ? '' : u.short_name
        },
      ],
    ),
  )
  const anonymousPostAuthors = computed<User[]>(() => postAuthors.value.filter((u) => u.registered === false))
  const totalPostAuthors = computed<number>(() => postAuthorIds.value.length)

  /* ---------------------- */
  /* GETTER FUNCTIONS       */
  /* ---------------------- */

  const getUserById = (id: Id | HashId): User | null => {
    return typeof id === 'number' ? userById.value[id] : userByHashId.value[id]
  }

  const getUserByHashId = async (hashId: HashId, wallId?: WallId): Promise<User | null> => {
    const user = userByHashId.value[hashId]
    if (user != null) {
      return user
    }

    // If user doesn't exist in cache and not being fetched, fetch it
    if (!isUserIdBeingFetched(hashId)) {
      await fetchUser(hashId, wallId)
    }

    return userByHashId.value[hashId]
  }

  const isUserIdBeingFetched = (id: Id | HashId): boolean => {
    return userIdsBeingFetched.value.includes(id)
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */

  const fetchUser = async (userId: Id | HashId, wallId?: WallId): Promise<void> => {
    if (getUserById(userId) != null || isUserIdBeingFetched(userId)) {
      return
    }

    userIdsBeingFetched.value.push(userId)
    try {
      const [{ WallUserContributorApi }, { UserContributorApi }] = await Promise.all([
        import('@@/surface/api/wall_user_contributor'),
        import('@@/surface/api/user_contributor'),
      ])
      const fetchedUserData =
        wallId != null ? await WallUserContributorApi.read(userId, wallId) : await UserContributorApi.read(userId)
      // TODO: consolidate User type
      const newUser = (fetchedUserData.data as JsonAPIResource<any>).attributes

      updateUser(newUser)

      userIdsBeingFetched.value = userIdsBeingFetched.value.filter((id) => id !== userId)
    } catch (e) {
      captureFetchException(e, { source: 'fetchUserContributorInfo' })
    }
  }

  // For the contributors panel (Currently it only shows post authors)
  const fetchPostAuthors = async (): Promise<void> => {
    if (postAuthorIds.value.length === 0) return

    try {
      postAuthorIds.value.forEach((userId) => {
        void fetchUser(userId)
      })
    } catch (e) {
      captureFetchException(e, { source: 'fetchPostAuthors' })
    }
  }

  const fetchAndWaitForAuthors = async (): Promise<void> => {
    if (postAuthorIds.value.length === 0) return
    try {
      await Promise.all(postAuthorIds.value.map(async (userId: Id): Promise<void> => await fetchUser(userId)))
    } catch (e) {
      captureFetchException(e, { source: 'fetchAndWaitForAuthors' })
    }
  }

  const showContributorsList = (): void => {
    xContributorsList.value = true
  }
  const hideContributorsList = (): void => {
    xContributorsList.value = false
  }

  const updateUser = (newUser: User): void => {
    vSet(userById.value, newUser.id, newUser)
    if (newUser.hashid != null) {
      vSet(userByHashId.value, newUser.hashid, newUser)
    }
  }

  // Usually post authors are loaded lazily when we need to display them on a post.
  // ie load posts -> display post -> load author
  // However if we group posts by author, the [display post] step doesn't happen as it's actually dependent on the [load author] step.
  // Hence we need to load authors in a different way:
  // Watch for when posts are loaded while grouped by author and fetch author data
  void unboundedWatch(
    [postAuthorIds, () => surfaceWishArrangementStore.isGroupByAuthor],
    async ([newPostAuthorIds, isGroupByAuthor], [oldPostAuthorIds = [], oldIsGroupByAuthor = false]) => {
      // Fetch post authors when:
      // 1. The wall is grouped by author
      // 2. We have post author IDs (posts have been loaded)
      // 3. Either:
      //    a. newPostAuthorIds contains an ID that is not in oldPostAuthorIds (new posts loaded)
      //    b. We just switched 'Group by' setting to be grouped by author (isGroupByAuthor changed from false to true)
      if (
        isGroupByAuthor &&
        newPostAuthorIds.length > 0 &&
        (newPostAuthorIds.some((id) => !oldPostAuthorIds.includes(id)) || !oldIsGroupByAuthor)
      ) {
        await fetchPostAuthors()
      }
    },
    { immediate: true },
  )

  return {
    // Getters
    postAuthorIds,
    postAuthors,
    postAuthorsById,
    postAuthorsByHashId,
    registeredPostAuthors,
    anonymousPostAuthors,
    totalPostAuthors,
    xContributorsList,
    areAuthorsFetched,

    // Getter functions
    getUserById,
    getUserByHashId,
    isUserIdBeingFetched,

    // Actions
    fetchUser,
    fetchPostAuthors,
    fetchAndWaitForAuthors,
    hideContributorsList,
    showContributorsList,
    updateUser,
  }
})
export { useSurfaceUserContributorsStore }
